/**
 * Sentiment Service
 * API calls for sentiment analysis and signals
 */

import { createApiClient } from '@shared/auth';
import apiConfig from '../config/apiConfig';
import { extractList, extractErrorMessage } from '../utils/responseHelpers';

const baseURL = apiConfig.getSentimentServiceUrl();

const client = createApiClient(baseURL);

/**
 * SSOT dashboard metrics — single source of truth for all KPIs and trends
 */
export const getDashboardSSOT = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/dashboard/metrics', { params: dateRange });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch dashboard metrics.') };
  }
};

/**
 * Get sentiment dashboard overview
 */
export const getSentimentOverview = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/trends/dashboard', { params: dateRange });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch sentiment data.') };
  }
};

/**
 * Get sentiment trends over time
 */
export const getSentimentTrends = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/trends', { params: dateRange });
    return { success: true, data: extractList(response.data, 'trends') };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch trends.') };
  }
};

/**
 * Get signal definitions
 */
export const getRecentSignals = async (params) => {
  try {
    const response = await client.get('/api/v1/signals', { params });
    return { success: true, data: extractList(response.data, 'signals') };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch signals.') };
  }
};

/**
 * Get analyses list with filters
 */
export const getAnalysesList = async (params) => {
  try {
    const response = await client.get('/api/v1/analyses', { params });
    const d = response.data;
    return {
      success: true,
      data: {
        analyses: extractList(d, 'analyses'),
        total: d?.total ?? 0,
      },
    };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch analyses.') };
  }
};

/**
 * Get top detected signals
 */
export const getTopSignals = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/analytics/top-signals', { params: dateRange });
    return { success: true, data: extractList(response.data, 'signals') };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch top signals.') };
  }
};

/**
 * Get contact reason breakdown
 */
export const getContactReasons = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/analytics/contact-reasons', { params: dateRange });
    return { success: true, data: extractList(response.data, 'reasons') };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch contact reasons.') };
  }
};

/**
 * Get disposition/outcome breakdown
 */
export const getDispositionBreakdown = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/analytics/disposition-breakdown', { params: dateRange });
    return { success: true, data: extractList(response.data, 'dispositions') };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch dispositions.') };
  }
};

/**
 * Analyze text for sentiment
 */
export const analyzeText = async (text) => {
  try {
    const response = await client.post('/api/v1/analyze', { text });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to analyze text.') };
  }
};

/**
 * Get transcript for an analysis
 */
export const getTranscript = async (analysisId) => {
  try {
    const response = await client.get(`/api/v1/analyses/${analysisId}/transcript`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch transcript.') };
  }
};

const sentimentServiceDefault = {
  getSentimentOverview,
  getSentimentTrends,
  getRecentSignals,
  getAnalysesList,
  analyzeText,
  getTopSignals,
  getContactReasons,
  getDispositionBreakdown,
  getTranscript,
};

export default sentimentServiceDefault;
